import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/TestAuthContext';
import axios from 'axios';
import AppLayout from '../components/AppLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface ComparisonData {
  report1: any;
  report2: any;
  differences: Array<{
    field: string;
    report1: number;
    report2: number;
    difference: number;
  }>;
  summary: {
    total_differences: number;
    final_claim_difference: number;
  };
}

export default function CompareReports() {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const [searchParams] = useSearchParams();
  
  const [reports, setReports] = useState<any[]>([]);
  const [selectedReport1, setSelectedReport1] = useState<string>(searchParams.get('r1') || '');
  const [selectedReport2, setSelectedReport2] = useState<string>(searchParams.get('r2') || '');
  const [comparison, setComparison] = useState<ComparisonData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadReports();
  }, []);

  useEffect(() => {
    if (selectedReport1 && selectedReport2) {
      compareReports();
    }
  }, [selectedReport1, selectedReport2]);

  const loadReports = async () => {
    try {
      const token = await getToken();
      const response = await axios.get(`${BACKEND_URL}/api/v1/reports`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReports(response.data.reports || []);
    } catch (err) {
      console.error('Failed to load reports:', err);
    }
  };

  const compareReports = async () => {
    if (!selectedReport1 || !selectedReport2) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const token = await getToken();
      const response = await axios.get(
        `${BACKEND_URL}/api/v1/reports/compare/${selectedReport1}/${selectedReport2}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setComparison(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to compare reports');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number | undefined | null) => {
    if (value === undefined || value === null) return 'N/A';
    return `$${Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <AppLayout>
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-xl font-bold text-gray-900">Compare Reports</h1>
          <p className="text-sm text-gray-500">Side-by-side comparison of claim calculations</p>
        </div>

        {/* Report Selection */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">Select Reports to Compare</h2>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Report 1</label>
              <select
                value={selectedReport1}
                onChange={(e) => setSelectedReport1(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select a report...</option>
                {reports.map((report) => {
                  const clientName = report.data?.client_name || report.client_name || 'Unknown Client';
                  const claimAmount = report.result?.final_claim_amount 
                    ? `$${Number(report.result.final_claim_amount).toLocaleString(undefined, {maximumFractionDigits: 0})}`
                    : report.status === 'draft' ? 'Draft' : 'Pending';
                  const shortId = report.id.substring(0, 8);
                  const date = new Date(report.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                  const fromBulk = report.data?.fromBulkUpload ? '📤' : '';
                  
                  return (
                    <option key={report.id} value={report.id}>
                      {fromBulk} {clientName} | {claimAmount} | {date} | ID: {shortId}
                    </option>
                  );
                })}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Report 2</label>
              <select
                value={selectedReport2}
                onChange={(e) => setSelectedReport2(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select a report...</option>
                {reports.map((report) => {
                  const clientName = report.data?.client_name || report.client_name || 'Unknown Client';
                  const claimAmount = report.result?.final_claim_amount 
                    ? `$${Number(report.result.final_claim_amount).toLocaleString(undefined, {maximumFractionDigits: 0})}`
                    : report.status === 'draft' ? 'Draft' : 'Pending';
                  const shortId = report.id.substring(0, 8);
                  const date = new Date(report.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                  const fromBulk = report.data?.fromBulkUpload ? '📤' : '';
                  
                  return (
                    <option key={report.id} value={report.id}>
                      {fromBulk} {clientName} | {claimAmount} | {date} | ID: {shortId}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>
        </div>

        {loading && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-500">Comparing reports...</p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg mb-6">
            {error}
          </div>
        )}

        {comparison && !loading && (
          <>
            {/* Summary */}
            <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
              <h2 className="text-lg font-semibold mb-4">Summary</h2>
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-blue-600 font-medium">Total Differences</p>
                  <p className="text-2xl font-bold text-blue-800">{comparison.summary.total_differences}</p>
                </div>
                <div className={`p-4 rounded-lg ${comparison.summary.final_claim_difference >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                  <p className={`text-sm font-medium ${comparison.summary.final_claim_difference >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    Final Claim Difference
                  </p>
                  <p className={`text-2xl font-bold ${comparison.summary.final_claim_difference >= 0 ? 'text-green-800' : 'text-red-800'}`}>
                    {formatCurrency(comparison.summary.final_claim_difference)}
                  </p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600 font-medium">Bulk Upload</p>
                  <p className="text-lg">
                    R1: {comparison.report1.from_bulk_upload ? '✅ Yes' : '❌ No'} | 
                    R2: {comparison.report2.from_bulk_upload ? '✅ Yes' : '❌ No'}
                  </p>
                </div>
              </div>
            </div>

            {/* Side by Side Comparison */}
            <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
              <h2 className="text-lg font-semibold mb-4">Side-by-Side Comparison</h2>
              
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="text-left p-3 border">Field</th>
                    <th className="text-right p-3 border">Report 1</th>
                    <th className="text-right p-3 border">Report 2</th>
                    <th className="text-right p-3 border">Difference</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Report Info */}
                  <tr className="bg-gray-50">
                    <td colSpan={4} className="p-3 font-semibold border">Report Info</td>
                  </tr>
                  <tr>
                    <td className="p-3 border">Title</td>
                    <td className="p-3 border text-right">{comparison.report1.title || 'N/A'}</td>
                    <td className="p-3 border text-right">{comparison.report2.title || 'N/A'}</td>
                    <td className="p-3 border text-right">-</td>
                  </tr>
                  <tr>
                    <td className="p-3 border">Created</td>
                    <td className="p-3 border text-right">{formatDate(comparison.report1.created_at)}</td>
                    <td className="p-3 border text-right">{formatDate(comparison.report2.created_at)}</td>
                    <td className="p-3 border text-right">-</td>
                  </tr>

                  {/* Revenue by Year */}
                  <tr className="bg-gray-50">
                    <td colSpan={4} className="p-3 font-semibold border">Revenue by Year</td>
                  </tr>
                  {['2019', '2020', '2021', '2022'].map(year => {
                    const r1 = comparison.report1.inputs.revenue_by_year[year] || 0;
                    const r2 = comparison.report2.inputs.revenue_by_year[year] || 0;
                    const diff = r2 - r1;
                    return (
                      <tr key={`rev-${year}`} className={diff !== 0 ? 'bg-yellow-50' : ''}>
                        <td className="p-3 border">Revenue {year}</td>
                        <td className="p-3 border text-right">{formatCurrency(r1)}</td>
                        <td className="p-3 border text-right">{formatCurrency(r2)}</td>
                        <td className={`p-3 border text-right ${diff !== 0 ? 'text-red-600 font-semibold' : ''}`}>
                          {diff !== 0 ? formatCurrency(diff) : '-'}
                        </td>
                      </tr>
                    );
                  })}

                  {/* Payroll by Year */}
                  <tr className="bg-gray-50">
                    <td colSpan={4} className="p-3 font-semibold border">Payroll by Year (from financial_data)</td>
                  </tr>
                  {['2019', '2020', '2021', '2022'].map(year => {
                    const p1 = comparison.report1.inputs.payroll_by_year[year] || 0;
                    const p2 = comparison.report2.inputs.payroll_by_year[year] || 0;
                    const diff = p2 - p1;
                    return (
                      <tr key={`pay-${year}`} className={diff !== 0 ? 'bg-yellow-50' : ''}>
                        <td className="p-3 border">Payroll {year}</td>
                        <td className="p-3 border text-right">{formatCurrency(p1)}</td>
                        <td className="p-3 border text-right">{formatCurrency(p2)}</td>
                        <td className={`p-3 border text-right ${diff !== 0 ? 'text-red-600 font-semibold' : ''}`}>
                          {diff !== 0 ? formatCurrency(diff) : '-'}
                        </td>
                      </tr>
                    );
                  })}

                  {/* Step 4 Payroll Inputs */}
                  <tr className="bg-gray-50">
                    <td colSpan={4} className="p-3 font-semibold border">Step 4 Payroll Inputs</td>
                  </tr>
                  {['2019', '2020', '2021', '2022'].map(year => {
                    const s1 = comparison.report1.inputs.step4_payroll[year] || 0;
                    const s2 = comparison.report2.inputs.step4_payroll[year] || 0;
                    const diff = Number(s2) - Number(s1);
                    return (
                      <tr key={`step4-${year}`} className={diff !== 0 ? 'bg-yellow-50' : ''}>
                        <td className="p-3 border">Step 4 Payroll {year}</td>
                        <td className="p-3 border text-right">{formatCurrency(Number(s1))}</td>
                        <td className="p-3 border text-right">{formatCurrency(Number(s2))}</td>
                        <td className={`p-3 border text-right ${diff !== 0 ? 'text-red-600 font-semibold' : ''}`}>
                          {diff !== 0 ? formatCurrency(diff) : '-'}
                        </td>
                      </tr>
                    );
                  })}

                  {/* Calculation Results */}
                  <tr className="bg-gray-50">
                    <td colSpan={4} className="p-3 font-semibold border">Calculation Results</td>
                  </tr>
                  {[
                    { key: 'benchmark_variable_profit', label: 'Benchmark Variable Profit' },
                    { key: 'coverage_variable_profit', label: 'Coverage Variable Profit' },
                    { key: 'variable_profit_loss', label: 'Variable Profit Loss' },
                    { key: 'fixed_payroll_baseline', label: 'Fixed Payroll Baseline' },
                    { key: 'rtp_adjustment', label: 'RTP Adjustment' },
                    { key: 'final_claim', label: 'Final Claim' },
                  ].map(({ key, label }) => {
                    const r1 = comparison.report1.results[key] || 0;
                    const r2 = comparison.report2.results[key] || 0;
                    const diff = r2 - r1;
                    return (
                      <tr key={key} className={diff !== 0 ? 'bg-yellow-50' : ''}>
                        <td className="p-3 border">{label}</td>
                        <td className="p-3 border text-right">{formatCurrency(r1)}</td>
                        <td className="p-3 border text-right">{formatCurrency(r2)}</td>
                        <td className={`p-3 border text-right ${diff !== 0 ? 'text-red-600 font-semibold' : ''}`}>
                          {diff !== 0 ? formatCurrency(diff) : '-'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Differences Highlight */}
            {comparison.differences.length > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
                <h2 className="text-lg font-semibold text-yellow-800 mb-4">⚠️ Key Differences Found</h2>
                <ul className="space-y-2">
                  {comparison.differences.map((diff, idx) => (
                    <li key={idx} className="flex justify-between items-center p-2 bg-white rounded border">
                      <span className="font-medium">{diff.field}</span>
                      <span>
                        R1: {formatCurrency(diff.report1)} → R2: {formatCurrency(diff.report2)}
                        <span className={`ml-2 font-semibold ${diff.difference >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          ({diff.difference >= 0 ? '+' : ''}{formatCurrency(diff.difference)})
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </AppLayout>
  );
}
